/**
 * openBrowserForTask — "Open the browser", for real (spec 2026-09-02 §3).
 *
 * One click from the story (the handoff card, the browser card at the foot of
 * a turn) opens the Computer on Chrome for this task AND wakes a sleeping
 * sandbox. The browser card used to emit BROWSER_OPEN_SESSION, which the
 * server is ruled never to wake on (pane demand is demand-only), so the person
 * clicked Open and met a lock screen. A person's click IS a human wake: it
 * rides BROWSER_WAKE, the one viewer-side signal that dials a hibernating
 * sandbox and opens Chrome (no target ⇒ openBrowser; the server registers the
 * pane demand itself), exactly as the Chrome window's own Open button does.
 *
 * The wake is stamped on the browser store so the lock veil blooms "Waking
 * up…" from the click frame and the card's dot goes gold before presence
 * catches up. Lives outside the stores: it touches two of them plus the
 * socket, and neither store should import the other.
 */
import { EVENTS } from "@bash/shared";
import { useBrowserStore } from "@/stores/browserStore";
import { useComputerStore } from "@/stores/computerStore";
import { getSocket } from "@/stores/socketService";
import { markBrowserOpenClicked } from "@/lib/open-path-metric";

/** A click's wake claim stands this long; a second click inside it only
 *  brings the pane forward (no second dispatch re-clearing the first wake's
 *  creds). Same stand as the handoff card's dot. */
export const WAKE_IN_FLIGHT_MS = 20_000;

/**
 * The wake half, on its own, so every open that should end in a live picture
 * runs the ONE rule instead of a copy of it: this one, openBrowserForTask's
 * own, and the composer's `open_computer` control when it lands on Chrome.
 * Takes no task — the wake stamp, the pane's mood and BROWSER_WAKE itself are
 * all the pane's, not a task's.
 *
 * Already live, or a wake already in flight: doing nothing is the whole job.
 * A BROWSER_WAKE here would re-enter waking on the server, clear the stream's
 * creds and re-mint them, dropping a live picture to "Opening Chrome…"
 * (review 09-02, S1).
 */
export function wakeBrowserIfAsleep(): void {
  const browser = useBrowserStore.getState();
  const inFlight =
    browser.wakeRequestedAt !== null &&
    Date.now() - browser.wakeRequestedAt < WAKE_IN_FLIGHT_MS;
  if (browser.paneMood === "live" || browser.viewSnapshot?.state === "live")
    return;
  if (
    inFlight ||
    browser.viewSnapshot?.state === "waking" ||
    browser.paneMood === "connecting"
  )
    return;
  browser.setWaking();
  browser.noteWakeRequested(Date.now());
  const socket = getSocket();
  if (!socket) return;
  markBrowserOpenClicked();
  socket.emit(EVENTS.BROWSER_WAKE);
}

export function openBrowserForTask(taskId: string): void {
  const browser = useBrowserStore.getState();
  browser.unparkPanelForTask(taskId);
  browser.openPanelForTask(taskId);
  // The person's own ask: the Computer opens on Chrome even over a sticky
  // manual close (the agent's SHOW verb has the same weight).
  useComputerStore.getState().showSurface(taskId, "browser");
  wakeBrowserIfAsleep();
}
